import {
  useActivateScene,
  useDeactivateScene,
  useListScenes,
  getListScenesQueryKey,
  getGetSceneQueryKey,
  type ScenesSceneSummaryResponse,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  GripVertical,
  List,
  Play,
  Plus,
  Square,
  Trash2,
} from 'lucide-react';
import { memo, useState, useMemo } from 'react';
import { z } from 'zod';

import type { ConfigFormProps, RendererProps, WidgetDefinition } from '../types';

import { useToast } from '@/components/ui';

/**
 * Scenes widget config schema
 */
export const scenesConfigSchema = z.object({
  sceneHumanIds: z.array(z.string()).min(1, 'At least one scene is required'),
});

export type ScenesConfig = z.infer<typeof scenesConfigSchema>;

/**
 * Represents a scene in the config list (may or may not exist in the API)
 */
interface ConfigSceneItem {
  humanId: string;
  name: string;
  exists: boolean;
  active?: boolean;
}

/**
 * Improved scenes configuration component with reordering and orphan management
 */
function ScenesConfigForm({ config, onChange, errors }: ConfigFormProps<ScenesConfig>) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { data: scenesData, isLoading } = useListScenes();

  const availableScenes = useMemo(() => scenesData?.scenes ?? [], [scenesData?.scenes]);

  // Build list of configured scenes with their details (including orphaned ones)
  const configuredScenes: ConfigSceneItem[] = useMemo(() => {
    return config.sceneHumanIds.map((humanId) => {
      const scene = availableScenes.find((s) => s.humanId === humanId);
      return {
        humanId,
        name: scene?.name ?? humanId,
        exists: !!scene,
        active: scene?.active,
      };
    });
  }, [config.sceneHumanIds, availableScenes]);

  // Scenes that haven't been added yet
  const unselectedScenes = useMemo(
    () => availableScenes.filter((s) => s.humanId && !config.sceneHumanIds.includes(s.humanId)),
    [availableScenes, config.sceneHumanIds]
  );

  const handleAddScene = (scene: ScenesSceneSummaryResponse) => {
    if (!scene.humanId) return;
    onChange({ ...config, sceneHumanIds: [...config.sceneHumanIds, scene.humanId] });
    setIsDropdownOpen(false);
  };

  const handleRemoveScene = (humanId: string) => {
    onChange({ ...config, sceneHumanIds: config.sceneHumanIds.filter((id) => id !== humanId) });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newIds = [...config.sceneHumanIds];
    const temp = newIds[index - 1]!;
    newIds[index - 1] = newIds[index]!;
    newIds[index] = temp;
    onChange({ ...config, sceneHumanIds: newIds });
  };

  const handleMoveDown = (index: number) => {
    if (index === config.sceneHumanIds.length - 1) return;
    const newIds = [...config.sceneHumanIds];
    const temp = newIds[index]!;
    newIds[index] = newIds[index + 1]!;
    newIds[index + 1] = temp;
    onChange({ ...config, sceneHumanIds: newIds });
  };

  const orphanCount = configuredScenes.filter((s) => !s.exists).length;

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Scenes
        </label>
        <p className="mb-3 text-xs text-gray-500 dark:text-zinc-500">
          Drag to reorder or use the arrow buttons. Scenes will display in this order.
        </p>

        {/* Orphaned scenes warning */}
        {orphanCount > 0 && (
          <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" />
            <span className="text-xs text-amber-700 dark:text-amber-400">
              {orphanCount} scene{orphanCount > 1 ? 's' : ''} no longer exist
              {orphanCount > 1 ? '' : 's'}. Remove {orphanCount > 1 ? 'them' : 'it'} to clear this
              warning.
            </span>
          </div>
        )}

        {/* Scene list */}
        {configuredScenes.length > 0 ? (
          <div className="space-y-1">
            {configuredScenes.map((scene, index) => (
              <div
                key={scene.humanId}
                className={`group flex items-center gap-2 rounded-lg border px-2 py-2 ${
                  scene.exists
                    ? 'border-gray-200 bg-white dark:border-white/[0.08] dark:bg-zinc-900'
                    : 'border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10'
                }`}
              >
                {/* Grip handle (visual only for now) */}
                <div className="flex-shrink-0 text-gray-300 dark:text-zinc-600">
                  <GripVertical className="h-4 w-4" />
                </div>

                {/* Scene info */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {scene.exists && scene.active && (
                      <span className="h-2 w-2 flex-shrink-0 rounded-full bg-emerald-500" />
                    )}
                    {!scene.exists && (
                      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                    )}
                    <span
                      className={`truncate text-sm font-medium ${
                        scene.exists
                          ? 'text-gray-900 dark:text-white'
                          : 'text-amber-700 dark:text-amber-400'
                      }`}
                    >
                      {scene.name}
                    </span>
                  </div>
                  {!scene.exists && (
                    <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-500">
                      Scene no longer exists
                    </p>
                  )}
                </div>

                {/* Reorder buttons */}
                <div className="flex flex-shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent dark:text-zinc-500 dark:hover:bg-white/[0.06] dark:hover:text-zinc-300"
                    title="Move up"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === configuredScenes.length - 1}
                    className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent dark:text-zinc-500 dark:hover:bg-white/[0.06] dark:hover:text-zinc-300"
                    title="Move down"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => handleRemoveScene(scene.humanId)}
                  className="flex-shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:text-zinc-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                  title="Remove scene"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center dark:border-white/[0.08] dark:bg-zinc-900/50">
            <List className="mx-auto mb-2 h-8 w-8 text-gray-300 dark:text-zinc-600" />
            <p className="text-sm text-gray-500 dark:text-zinc-500">No scenes added yet</p>
            <p className="mt-1 text-xs text-gray-400 dark:text-zinc-600">
              Add scenes using the button below
            </p>
          </div>
        )}

        {/* Add scene dropdown */}
        <div className="relative mt-3">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            disabled={unselectedScenes.length === 0 && !isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-300 disabled:hover:bg-white disabled:hover:text-gray-600 dark:border-white/[0.12] dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-blue-500/50 dark:hover:bg-blue-500/10 dark:hover:text-blue-400 dark:disabled:hover:border-white/[0.12] dark:disabled:hover:bg-zinc-900 dark:disabled:hover:text-zinc-400"
          >
            <Plus className="h-4 w-4" />
            {isLoading
              ? 'Loading scenes...'
              : unselectedScenes.length === 0
                ? 'All scenes added'
                : 'Add scene'}
          </button>

          {isDropdownOpen && unselectedScenes.length > 0 && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} />
              <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
                {unselectedScenes.map((scene) => (
                  <button
                    key={scene.id}
                    type="button"
                    onClick={() => handleAddScene(scene)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                  >
                    {scene.active && <span className="h-2 w-2 rounded-full bg-emerald-500" />}
                    <span className="text-gray-900 dark:text-white">{scene.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Error message */}
        {errors?.['sceneHumanIds'] && (
          <p className="mt-2 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-3.5 w-3.5" />
            {errors['sceneHumanIds']}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Individual scene row in the list
 */
function SceneRow({ scene }: { scene: ScenesSceneSummaryResponse }) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { mutate: activateScene, isPending: isActivating } = useActivateScene({
    mutation: {
      onSuccess: () => {
        addToast({ message: `Scene "${scene.name}" activated`, variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
        if (scene.id) {
          queryClient.invalidateQueries({ queryKey: getGetSceneQueryKey(scene.id) });
        }
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to activate scene',
          variant: 'error',
        });
      },
    },
  });

  const { mutate: deactivateScene, isPending: isDeactivating } = useDeactivateScene({
    mutation: {
      onSuccess: () => {
        addToast({ message: `Scene "${scene.name}" deactivated`, variant: 'success' });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
        if (scene.id) {
          queryClient.invalidateQueries({ queryKey: getGetSceneQueryKey(scene.id) });
        }
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to deactivate scene',
          variant: 'error',
        });
      },
    },
  });

  const isPending = isActivating || isDeactivating;

  const handleToggle = () => {
    if (!scene.id) return;
    if (scene.active) {
      deactivateScene({ id: scene.id });
    } else {
      activateScene({ id: scene.id, data: {} });
    }
  };

  return (
    <div
      className={`flex items-center justify-between rounded-lg px-3 py-2 ${
        scene.active ? 'bg-emerald-50 dark:bg-emerald-500/10' : 'bg-gray-50 dark:bg-zinc-800/50'
      }`}
    >
      <span className="text-sm font-medium text-gray-900 dark:text-white">{scene.name}</span>
      <button
        onClick={handleToggle}
        disabled={isPending}
        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
          scene.active
            ? 'bg-emerald-500 text-white hover:bg-emerald-600'
            : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600'
        } ${isPending ? 'animate-pulse' : ''}`}
      >
        {isPending ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : scene.active ? (
          <Square className="h-4 w-4" />
        ) : (
          <Play className="ml-0.5 h-4 w-4" />
        )}
      </button>
    </div>
  );
}

/**
 * Renderer for Scenes widget
 */
const ScenesRenderer = memo(function ScenesRenderer({ config }: RendererProps<ScenesConfig>) {
  const { data: scenesData, isLoading } = useListScenes({
    query: {
      refetchInterval: 10000,
    },
  });

  const allScenes = useMemo(() => scenesData?.scenes ?? [], [scenesData?.scenes]);

  // Preserve order from config by mapping humanIds to scenes
  const orderedScenes = useMemo(() => {
    return config.sceneHumanIds
      .map((humanId) => allScenes.find((s) => s.humanId === humanId))
      .filter((s): s is ScenesSceneSummaryResponse => s !== undefined);
  }, [config.sceneHumanIds, allScenes]);

  const missingIds = useMemo(
    () => config.sceneHumanIds.filter((id) => !allScenes.some((s) => s.humanId === id)),
    [config.sceneHumanIds, allScenes]
  );

  if (config.sceneHumanIds.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-zinc-500">No scenes configured</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  const rowCount = orderedScenes.length + (missingIds.length > 0 ? 1 : 0);

  return (
    <div
      className="grid h-full gap-2"
      style={{ gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))` }}
    >
      {orderedScenes.map((scene) => (
        <SceneRow key={scene.id} scene={scene} />
      ))}
      {missingIds.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-xs text-amber-700 dark:text-amber-400">
            {missingIds.length} scene{missingIds.length > 1 ? 's' : ''} not found
          </span>
        </div>
      )}
    </div>
  );
});

/**
 * Scenes widget definition
 */
export const scenesWidget: WidgetDefinition<ScenesConfig> = {
  type: 'scenes',
  displayName: 'Scenes List',
  description: 'Display and control multiple scenes',
  icon: List,
  defaultSize: { w: 3, h: 4 },
  minSize: { w: 2, h: 2 },
  maxSize: { w: 6, h: 8 },
  configSchema: scenesConfigSchema,
  defaultConfig: {
    sceneHumanIds: [],
  },
  ConfigForm: ScenesConfigForm,
  Renderer: ScenesRenderer,
};
